import React from 'react';
import { useParams } from 'react-router-dom';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';

/**
 * Sticky banner shown while a platform superadmin is operating with a
 * tenant-scoped, tenant-admin token in its own browser window. The real actor is
 * retained by the API for audit, but the workspace uses the same permissions
 * and UI as a tenant admin.
 *
 * Self-hides for: non-superadmins, platform routes, and the reserved
 * platform tenant.
 */
const PLATFORM_TENANT_ID = '00000000-0000-0000-0000-000000000000';

const SuperadminImpersonationBanner = () => {
  const { tenantId } = useParams();
  const user = useAuthStore((state) => state.user);

  const shouldShow = Boolean(
    user?.is_superadmin_actor && user?.tenant_admin_mode &&
    tenantId && tenantId !== PLATFORM_TENANT_ID
  );

  if (!shouldShow) return null;

  // select-tenant already returns the tenant name in the authenticated user
  // payload. Reading it here avoids calling the superadmin-only tenant-detail
  // endpoint with the deliberately tenant-scoped impersonation token.
  const tenantName = user?.tenant_name || tenantId.slice(0, 8) + '…';

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-30 flex items-center gap-3 px-4 py-2 bg-blue-50 text-blue-900 border-b border-blue-200 dark:bg-blue-900/30 dark:text-blue-100 dark:border-blue-800"
    >
      <div className="flex items-center gap-2 min-w-0">
        <BuildingOffice2Icon className="w-5 h-5 flex-shrink-0" />
        <span className="text-sm font-medium truncate">
          <span className="font-semibold">Tenant Admin workspace</span> ·{' '}
          <span className="font-semibold">{tenantName}</span>
        </span>
      </div>
    </div>
  );
};

export default SuperadminImpersonationBanner;
